import { Button, Input } from "components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { Link } from "react-router-dom";
import { Routes } from "router";
import { useEffect } from "react";
import { PrecisionLogoMain } from "assets";

export interface ResetData {
  password: string;
  confirmPassword: string;
}

const initResetData: ResetData = {
  password: "",
  confirmPassword: "",
};

const schema = yup.object({
  password: yup
    .string()
    .required("Required")
    .min(8, "Password should be at least 8 characters long")
    .matches(/[A-Z]/, "Password should contain an uppercase character")
    .matches(/[a-z]/, "Password should contain an lowercase character")
    .matches(/[0-9]/, "Password should contain at least one number")
    .matches(
      /[@#&$]/,
      "Password should contain at least special character (e.g. @, #, &, $)"
    ),
  confirmPassword: yup
    .string()
    .required("Required")
    .oneOf([yup.ref("password")], "Passwords must match"),
});

interface SigninProps {
  submit: (data: ResetData) => void;
  clear: boolean;
}

const NewPasswordUI: React.FC<SigninProps> = ({ submit, clear }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ResetData>({
    resolver: yupResolver(schema),
    defaultValues: initResetData,
  });

  useEffect(() => {
    reset(initResetData);
  }, [clear]);

  const onSubmit: SubmitHandler<ResetData> = (data) => {
    submit(data);
  };

  return (
    <div className="">
      <div className="bg-[#26279A] h-[320px] pt-[40px] px-5 lg:hidden flex flex-col">
        <Link to={Routes.home} className="w-fit">
          <PrecisionLogoMain className='w-[180px] flex-shrink-0' />
        </Link>
        <h1 className='text-[23px] font-bold mt-5 text-white w-fit lg:hidden flex'>
          Reset Password
        </h1>
        <p className="text-white text-sm mt-4 lg:text-center lg:hidden flex">
          Your new password must be different from previous used passwords
        </p>
      </div>
      <div className="lg:w-full w-[90%] mx-auto lg:mt-0 -mt-[80px] rounded-xl lg:p-0 p-4 bg-[#E8F3FF] sm:mb-0 mb-8">
        <h1 className='lg:text-[27px] text-xl font-bold lg:mt-9 mt-6 text-[#012169] w-fit lg:mx-auto lg:block hidden'>
          Reset Password
        </h1>
        <p className="text-[#475467] text-sm mt-4 lg:text-center lg:flex hidden">
          Your new password must be different from previous used passwords
        </p>
        <form className='lg:mt-7'>
          <Input
            label="Password"
            placeholder="Enter password"
            type="password"
            required
            validatorMessage={errors.password?.message}
            name="password"
            register={register}
            value={watch("password")}
            parentClassName="[&>label]:text-[#344054] [&>label]:font-light !mb-2"
            className="!bg-white !border-[#EAECF0] !shadow-none"
            iconClassName="!w-4 !h-4 !top-11"
          />
          <Input
            label="Confirm Password"
            placeholder="Confirm password"
            type="password"
            required
            validatorMessage={errors.confirmPassword?.message}
            name="confirmPassword"
            register={register}
            value={watch("confirmPassword")}
            showRequired
            parentClassName="[&>label]:text-[#344054] [&>label]:font-light"
            className="!bg-white !border-[#EAECF0] !shadow-none"
            iconClassName="!w-4 !h-4 !top-11"
          />

          <Button
            className='!bg-[#012169] !border-none w-full text-white !hover:bg-[#012169] !hover:opacity-60 mt-6 mb-3 !py-3 !text-[15px] !rounded-[5px]'
            type="fill"
            onClick={handleSubmit(onSubmit)}
          >
            Reset Password
          </Button>
        </form>

        <p className='text-[#2B4682] mx-auto w-fit pt-1'>
          Go back? <Link to={Routes.signin} className="text-[#012169] font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export { NewPasswordUI };
